// React-биндинг к client-store (SPEC §4.0). client-store отдаёт императивный фасад
// (select/actions/subscribe) — здесь его React-обёртка: контекст со store + `useSelector` поверх
// `useSyncExternalStore` (стабильно при concurrent-render, без тиринга). Это «React-биндинг поверх
// subscribe», на который ссылается комментарий client-store.
//
// Никакой сети/доменной логики тут нет: только связывание стора с деревом React.

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import type { ClientStore } from '@tele-chat/client-store'

const StoreContext = createContext<ClientStore | null>(null)

export interface StoreProviderProps {
  store: ClientStore
  children: ReactNode
}

/** Делает store доступным поддереву (создаётся один раз в App, §4.0). */
export function StoreProvider({ store, children }: StoreProviderProps): JSX.Element {
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
}

/** Доступ к императивному фасаду стора (select/actions). Бросает вне StoreProvider. */
export function useStore(): ClientStore {
  const store = useContext(StoreContext)
  if (store === null) {
    throw new Error('useStore(): компонент должен быть внутри <StoreProvider>')
  }
  return store
}

/**
 * Подписка на проекцию стора через селектор. Пересчитывает результат на каждое уведомление стора,
 * но React ре-рендерит только при изменении значения (Object.is). Для ссылочно-нестабильных
 * результатов передайте `isEqual` (напр. сравнение по длине/полям) — селекторы timeline/chatList
 * стора уже ссылочно-стабильны по ревизии чата (client-store §4.1), так что дефолт Object.is им подходит.
 */
/**
 * Структурное равенство ограниченной глубины (2 уровня): массивы по длине+элементам, объекты по
 * ключам, на дне — Object.is. Глубина 2 нужна, чтобы массив МЕЛКИХ объектов-значений (typingIn →
 * [{userId,kind}], presence/receipts-образные) считался равным при том же содержимом: иначе селектор
 * отдаёт новые ссылки каждый вызов, getSnapshot никогда не стабилизируется и useSyncExternalStore
 * уходит в бесконечный ре-рендер (React #185, белый экран на живом «печатает…»). Уже ссылочно-стабильные
 * селекторы (timeline/chatList по ревизии) проходят по Object.is на верхнем уровне и до рекурсии не доходят.
 */
function equalUpTo(a: unknown, b: unknown, depth: number): boolean {
  if (Object.is(a, b)) return true
  if (depth <= 0) return false
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    for (let i = 0; i < a.length; i += 1) if (!equalUpTo(a[i], b[i], depth - 1)) return false
    return true
  }
  const ao = a as Record<string, unknown>
  const bo = b as Record<string, unknown>
  const ka = Object.keys(ao)
  const kb = Object.keys(bo)
  if (ka.length !== kb.length) return false
  for (const k of ka) if (!equalUpTo(ao[k], bo[k], depth - 1)) return false
  return true
}

function shallowEqual<T>(a: T, b: T): boolean {
  return equalUpTo(a, b, 2)
}

export function useSelector<T>(selector: (store: ClientStore) => T, isEqual?: (a: T, b: T) => boolean): T {
  const store = useStore()
  const lastRef = useRef<{ value: T } | null>(null)

  const getSnapshot = useCallback((): T => {
    const next = selector(store)
    const prev = lastRef.current
    if (prev !== null) {
      const same = isEqual ? isEqual(prev.value, next) : shallowEqual(prev.value, next)
      if (same) return prev.value
    }
    lastRef.current = { value: next }
    return next
  }, [store, selector, isEqual])

  const subscribe = useCallback((onChange: () => void) => store.subscribe(onChange), [store])

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Удобный хук: только actions (мутации) — без подписки на состояние. */
export function useActions(): ClientStore['actions'] {
  return useStore().actions
}
